// src/hooks/usePhotos.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { uploadPhotoToR2, deletePhotoFromR2 } from '@/lib/r2Photos';
import { checkChildrenConsentStatus } from '@/hooks/useConsents';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { Sentry } from '@/lib/sentry';

/** Run fn up to retries times with delayMs between attempts. */
async function withRetry(fn, { retries = 3, delayMs = 1200 } = {}) {
  let lastError;
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      if (attempt < retries) {
        logger.debug('Photo upload attempt failed, retrying', { attempt, retries, message: err?.message });
        await new Promise((r) => setTimeout(r, delayMs));
      }
    }
  }
  throw lastError;
}

// =====================================================
// QUERIES
// =====================================================

/**
 * Get photos for specific group
 */
export const useGroupPhotos = (groupId, options = {}) => {
  return useQuery({
    queryKey: ['photos', 'group', groupId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('photos')
        .select('*')
        .eq('group_id', groupId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!groupId,
    ...options
  });
};

/**
 * Get photos for user's children (parent view)
 * Filters out photos where any tagged child's parents haven't consented
 * Only returns photos if user has photo consent AND all tagged children have all parents with consent
 */
export const useMyChildrenPhotos = (groupIds = [], options = {}) => {
  return useQuery({
    queryKey: ['photos', 'my-children', groupIds],
    queryFn: async () => {
      if (!groupIds || groupIds.length === 0) return [];

      const { data: photos, error } = await supabase
        .from('photos')
        .select('*')
        .in('group_id', groupIds)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      if (!photos || photos.length === 0) return [];

      // Filter photos by per-child consent status
      // Photos with no tagged children are visible (untagged photos)
      // Photos with tagged children: only show if ALL children have ALL parents with consent
      const filteredPhotos = [];
      
      for (const photo of photos) {
        const childIds = photo.child_ids || [];
        
        // Untagged photos are visible
        if (childIds.length === 0) {
          filteredPhotos.push(photo);
          continue;
        }

        // Check consent for all tagged children
        const consentCheck = await checkChildrenConsentStatus(childIds);
        if (consentCheck.allConsented) {
          filteredPhotos.push(photo);
        }
        // If not all consented, photo is filtered out (hidden from parents)
      }

      return filteredPhotos;
    },
    enabled: groupIds && groupIds.length > 0,
    ...options
  });
};

/**
 * Get single photo
 */
export const usePhoto = (photoId, options = {}) => {
  return useQuery({
    queryKey: ['photos', photoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('photos')
        .select('*')
        .eq('id', photoId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!photoId,
    ...options
  });
};

// =====================================================
// MUTATIONS
// =====================================================

/**
 * Upload photo to R2 and create database record.
 * Uses presigned URL flow via r2-presigned-upload Edge Function.
 * Validates consent for all tagged children before upload.
 */
export const useUploadPhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, groupId, branchId, caption, childIds = [] }) => {
      // Validate consent BEFORE uploading to R2
      if (childIds && childIds.length > 0) {
        const consentCheck = await checkChildrenConsentStatus(childIds);
        if (!consentCheck.allConsented) {
          const childNames = consentCheck.childrenWithoutConsent
            .map(c => c.childName || c.childId)
            .join(', ');
          throw new Error(`CONSENT_VALIDATION_FAILED:${JSON.stringify(consentCheck.childrenWithoutConsent)}`);
        }
      }

      const fileExt = file.name.split('.').pop();
      const uniqueId = `${Date.now()}-${Math.random().toString(36).substring(7)}`;
      const fileName = `${branchId}/${groupId}/${uniqueId}.${fileExt}`;
      const filePath = `photos/${fileName}`;

      const publicUrl = await uploadPhotoToR2(filePath, file, file.type || 'image/jpeg');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: photo, error: dbError } = await supabase
        .from('photos')
        .insert({
          storage_path: filePath,
          file_url: publicUrl,
          file_name: file.name,
          file_size: file.size,
          mime_type: file.type,
          uploader_id: user.id,
          group_id: groupId,
          branch_id: branchId,
          caption: caption || null,
          child_ids: childIds,
          taken_at: new Date().toISOString(),
          storage_provider: 'r2'
        })
        .select()
        .single();

      if (dbError) throw dbError;
      return photo;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['photos'] });
      toast.success('Photo uploaded successfully!');
    },
    onError: (error) => {
      logger.error('Upload photo error', { message: error.message });
      toast.error(`Failed to upload photo: ${error.message}`);
    }
  });
};

/**
 * Upload multiple photos with retries and optional progress callback.
 * Uses R2 presigned URL flow per file. Uploads sequentially so progress is accurate; each upload is retried up to 3 times.
 * Validates consent for all tagged children before upload.
 * @param {Array} photos - Array of { file: File, caption: string, childIds: Array }
 * @param {Function} [onProgress] - Optional (completed, total, phase) => {} where phase is 'uploading' | 'saving'
 */
export const useBulkUploadPhotos = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ photos, groupId, branchId, onProgress }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Validate consent for all photos BEFORE any R2 uploads
      const allChildIds = [...new Set(photos.flatMap(p => p.childIds || []))];
      if (allChildIds.length > 0) {
        const consentCheck = await checkChildrenConsentStatus(allChildIds);
        if (!consentCheck.allConsented) {
          throw new Error(`CONSENT_VALIDATION_FAILED:${JSON.stringify(consentCheck.childrenWithoutConsent)}`);
        }
      }

      const total = photos.length;
      const baseTimestamp = Date.now();
      const successful = [];
      const failed = [];

      for (let index = 0; index < photos.length; index++) {
        const photo = photos[index];
        const { file, caption, childIds = [] } = photo;
        const fileExt = file.name.split('.').pop();
        const uniqueId = `${baseTimestamp}-${index}-${Math.random().toString(36).substring(7)}`;
        const fileName = `${branchId}/${groupId}/${uniqueId}.${fileExt}`;
        const filePath = `photos/${fileName}`;

        try {
          const publicUrl = await withRetry(
            () => uploadPhotoToR2(filePath, file, file.type || 'image/jpeg'),
            { retries: 3, delayMs: 1200 }
          );
          successful.push({
            storage_path: filePath,
            file_url: publicUrl,
            file_name: file.name,
            file_size: file.size,
            mime_type: file.type,
            uploader_id: user.id,
            group_id: groupId,
            branch_id: branchId,
            caption: caption || null,
            child_ids: childIds.length > 0 ? childIds : null,
            taken_at: new Date().toISOString(),
            storage_provider: 'r2'
          });
        } catch (err) {
          failed.push({ file: file.name, error: err });
        }

        if (typeof onProgress === 'function') {
          onProgress(successful.length + failed.length, total, 'uploading');
        }
      }

      if (typeof onProgress === 'function') {
        onProgress(total, total, 'saving');
      }

      if (successful.length > 0) {
        const { data, error } = await supabase.from('photos').insert(successful).select();
        if (error) {
          logger.error('Database insert error:', error);
          throw error;
        }
        return { successful: data, failed, total: photos.length, successCount: successful.length, failedCount: failed.length };
      }
      const firstReason = failed[0]?.error?.message ?? (failed[0]?.error && String(failed[0].error));
      if (typeof Sentry?.setContext === 'function') {
        Sentry.setContext('upload_failures', {
          count: failed.length,
          firstError: firstReason,
          details: failed.map((f) => ({ file: f.file, error: f.error?.message ?? String(f.error) })),
        });
      }
      throw new Error(firstReason ? `All photo uploads failed: ${firstReason}` : 'All photo uploads failed');
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['photos'] });
      if (result.failedCount > 0) {
        toast.warning(`${result.successCount} z ${result.total} zdjęć zostało przesłanych. ${result.failedCount} nie powiodło się.`);
      } else {
        toast.success(`${result.successCount} zdjęć zostało przesłanych pomyślnie!`);
      }
    },
    onError: (error) => {
      // Consent validation is handled in UI; don't log as error or show toast
      if (error.message?.includes('CONSENT_VALIDATION_FAILED:')) {
        logger.debug('Bulk upload blocked by consent validation');
        return;
      }
      logger.error('Bulk upload error', { message: error.message });
      toast.error(`Nie udało się przesłać zdjęć: ${error.message}`);
    }
  });
};

/**
 * Update photo metadata
 */
export const useUpdatePhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }) => {
      const { data, error } = await supabase
        .from('photos')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['photos'] });
      toast.success('Photo updated!');
    },
    onError: (error) => {
      logger.error('Update photo error', { message: error.message });
      toast.error(`Failed to update: ${error.message}`);
    }
  });
};

/**
 * Delete photo (from storage and database).
 * R2: uses r2-delete-photo Edge Function. Supabase: user-uploads or legacy photos bucket.
 */
export const useDeletePhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (photoId) => {
      const { data: photo, error: fetchError } = await supabase
        .from('photos')
        .select('storage_path, storage_provider')
        .eq('id', photoId)
        .single();

      if (fetchError) throw fetchError;

      if (photo.storage_provider === 'r2') {
        await deletePhotoFromR2(photoId);
      } else if (photo.storage_path) {
        const isNewFormat = photo.storage_path?.startsWith('photos/');
        const bucket = isNewFormat ? 'user-uploads' : 'photos';
        const { error: storageError } = await supabase.storage.from(bucket).remove([photo.storage_path]);
        if (storageError) throw storageError;
      }

      const { error: dbError } = await supabase.from('photos').delete().eq('id', photoId);
      if (dbError) throw dbError;
      return photoId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['photos'] });
      toast.success('Photo deleted successfully!');
    },
    onError: (error) => {
      logger.error('Delete photo error', { message: error.message });
      toast.error(`Failed to delete photo: ${error.message}`);
    }
  });
};

/**
 * Tag children in photo
 */
export const useTagChildrenInPhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ photoId, childIds }) => {
      const { data, error } = await supabase
        .from('photos')
        .update({ child_ids: childIds })
        .eq('id', photoId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['photos'] });
      toast.success('Oznaczenia zaktualizowane.');
    },
    onError: (error) => {
      logger.error('Tag children error', { message: error.message });
      toast.error('Nie udało się zaktualizować oznaczeń.');
    }
  });
};

/**
 * Check consent status for photos and return warnings for revoked consent
 * Used by teachers to see which photos have consent issues
 * @param {Array} photos - Array of photo objects with child_ids
 * @returns {Promise<Map<string, Array>>} Map of photoId -> array of children without consent
 */
export async function checkPhotosConsentStatus(photos) {
  if (!photos || photos.length === 0) return new Map();

  const warnings = new Map();

  for (const photo of photos) {
    const childIds = photo.child_ids || [];
    
    // Untagged photos have no consent issues
    if (childIds.length === 0) continue;

    const consentCheck = await checkChildrenConsentStatus(childIds);
    if (!consentCheck.allConsented && consentCheck.childrenWithoutConsent.length > 0) {
      warnings.set(photo.id, consentCheck.childrenWithoutConsent);
    }
  }

  return warnings;
}

// =====================================================
// AVATAR/PROFILE PHOTO UPLOAD (DRY pattern)
// =====================================================

/**
 * Upload avatar/profile photo to user-uploads bucket
 * Reusable for user profiles, children photos, etc.
 * 
 * For child photos, organizes files by branch_id folder: avatars/{branchId}/{entityId}-{timestamp}.jpg
 * For user avatars, uses root avatars folder: avatars/{entityId}-{timestamp}.jpg
 *
 * @example
 * const uploadAvatar = useUploadAvatar();
 *
 * // Upload user profile avatar (no branch_id needed)
 * await uploadAvatar.mutateAsync({
 *   file: croppedFile,
 *   folder: 'avatars',
 *   entityId: userId,
 *   table: 'app_users',
 *   column: 'avatar_url'
 * });
 *
 * // Upload child photo (with branch_id for organization)
 * await uploadAvatar.mutateAsync({
 *   file: croppedFile,
 *   folder: 'avatars',
 *   entityId: childId,
 *   table: 'children',
 *   column: 'photo_url',
 *   branchId: branchId
 * });
 */
export const useUploadAvatar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, folder, entityId, table, column, branchId, userRole, userProfile }) => {
      // 1. Generate unique file name
      const fileName = `${entityId}-${Date.now()}.jpg`;
      
      // 2. Build file path based on entity type and user role
      let filePath;
      
      if (table === 'children') {
        // Child photos: always use branch_id folder
        // avatars/{branchId}/{entityId}-{timestamp}.jpg
        if (!branchId) {
          throw new Error('branchId is required for child photos');
        }
        filePath = `${folder}/${branchId}/${fileName}`;
      } else if (table === 'app_users') {
        // User avatars: determine folder based on role
        const role = userRole || userProfile?.primary_role || userProfile?.active_role;
        
        if (role === 'super_admin') {
          // Superadmin: avatars/{entityId}-{timestamp}.jpg (no branch folder)
          filePath = `${folder}/${fileName}`;
        } else {
          // Branch admin or parent: avatars/{branchId}/{entityId}-{timestamp}.jpg
          // Get branchId from userProfile if not provided
          const userBranchId = branchId || 
                              userProfile?.current_branch_id || 
                              userProfile?.branch_ids?.[0] || 
                              userProfile?.parent_branch_id;
          
          if (!userBranchId) {
            throw new Error('branchId is required for branch_admin and parent avatars');
          }
          filePath = `${folder}/${userBranchId}/${fileName}`;
        }
      } else {
        // Fallback: use root folder
        filePath = `${folder}/${fileName}`;
      }

      // 3. Upload to Supabase Storage (user-uploads bucket)
      const { error: uploadError } = await supabase.storage
        .from('user-uploads')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // 4. Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('user-uploads')
        .getPublicUrl(filePath);

      // 5. Update database record
      const { error: updateError } = await supabase
        .from(table)
        .update({ [column]: publicUrl })
        .eq('id', entityId);

      if (updateError) throw updateError;

      return { publicUrl, filePath };
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: [variables.table] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast.success('Zdjęcie zapisane');
    },
    onError: (error) => {
      logger.error('Upload avatar error', { message: error.message });
      toast.error('Nie udało się przesłać zdjęcia');
    }
  });
};

/**
 * Standalone function for uploading avatar without react-query
 * Use this when you don't need query invalidation or prefer async/await pattern
 * 
 * For child photos, organizes files by branch_id folder: avatars/{branchId}/{entityId}-{timestamp}.jpg
 * For user avatars, uses root avatars folder: avatars/{entityId}-{timestamp}.jpg
 */
export async function uploadAvatarPhoto({ file, folder, entityId, table, column, branchId, userRole, userProfile }) {
  // 1. Generate unique file name
  const fileName = `${entityId}-${Date.now()}.jpg`;
  
  // 2. Build file path based on entity type and user role
  let filePath;

  if (table === 'children') {
    // Child photos: always use branch_id folder
    // avatars/{branchId}/{entityId}-{timestamp}.jpg
    if (!branchId) {
      throw new Error('branchId is required for child photos');
    }
    filePath = `${folder}/${branchId}/${fileName}`;
  } else if (table === 'app_users') {
    // User avatars: determine folder based on role
    const role = userRole || userProfile?.primary_role || userProfile?.active_role;
    
    if (role === 'super_admin') {
      // Superadmin: avatars/{entityId}-{timestamp}.jpg (no branch folder)
      filePath = `${folder}/${fileName}`;
    } else {
      // Branch admin or parent: avatars/{branchId}/{entityId}-{timestamp}.jpg
      // Get branchId from userProfile if not provided
      const userBranchId = branchId || 
                          userProfile?.current_branch_id || 
                          userProfile?.branch_ids?.[0] || 
                          userProfile?.parent_branch_id;
      
      if (!userBranchId) {
        throw new Error('branchId is required for branch_admin and parent avatars');
      }
      filePath = `${folder}/${userBranchId}/${fileName}`;
    }
  } else {
    // Fallback: use root folder
    filePath = `${folder}/${fileName}`;
  }

  logger.debug('uploadAvatarPhoto starting', { folder, table, column, branchId, filePath });

  // 3. Upload to Supabase Storage (user-uploads bucket)
  const { error: uploadError } = await supabase.storage
    .from('user-uploads')
    .upload(filePath, file);

  if (uploadError) {
    logger.error('Storage upload failed', { message: uploadError.message });
    throw uploadError;
  }
  logger.debug('Storage upload successful');

  // 3. Get public URL
  const { data: { publicUrl } } = supabase.storage
    .from('user-uploads')
    .getPublicUrl(filePath);
  logger.debug('Public URL generated');

  // 4. Update database record
  const { data: updateData, error: updateError } = await supabase
    .from(table)
    .update({ [column]: publicUrl })
    .eq('id', entityId)
    .select();  // Return the updated row to verify it worked

  logger.debug('Database update completed', { success: !updateError });

  if (updateError) {
    logger.error('Database update failed', { message: updateError.message });
    throw updateError;
  }

  if (!updateData || updateData.length === 0) {
    logger.error('Database update returned no rows - RLS might be blocking the update');
    throw new Error('Nie można zaktualizować zdjęcia - brak uprawnień');
  }

  logger.debug('Database update successful');

  return { publicUrl, filePath };
}
